import type { AxiosResponse } from 'axios';

import { logger } from '@utils/logger';
import type { WeMetConnectionRead } from '@models/api/we-met-connection';

const ENRICHMENT_KEYS = ['user', 'event_name', 'space_name'] as const;

/**
 * Dev-only tripwire for the v2 We-Met enriched-response contract (backend #537).
 *
 * Every v2 endpoint (create / confirm / decline / list) serializes `user`,
 * `event_name`, and `space_name` — even when null (FastAPI drops no null keys in
 * this response chain; there is no `exclude_none`). So a *missing* key uniquely
 * means we're talking to a pre-#537 backend, whereas a null *value* is legitimate
 * (removed account / venue withheld from the viewer). We warn on the former — the
 * deploy-order slip that renders blank "Someone you met" cards — WITHOUT any
 * client-side fallback (re-introducing one was the #920 / #921 bug this guards).
 *
 * Warn, not throw, and DEV-only: a graceful-degradation tripwire, never a hard
 * guarantee. Production stays silent and renders whatever it received.
 */
export function assertWeMetV2Enriched(row: WeMetConnectionRead): void {
  if (!import.meta.env.DEV) return;
  const missing = ENRICHMENT_KEYS.filter(key => !(key in row));
  if (missing.length > 0) {
    logger.warn('[wemet-v2] response row is missing enrichment keys — pre-#537 backend?', {
      id: row.id,
      missing,
    });
  }
}

/** `.then()` tap: check a single-row v2 response, then pass it through unchanged. */
export function tapWeMetV2Row(
  response: AxiosResponse<WeMetConnectionRead>,
): AxiosResponse<WeMetConnectionRead> {
  if (response.data) assertWeMetV2Enriched(response.data);
  return response;
}

/** `.then()` tap: check every row of a v2 list response, then pass it through. */
export function tapWeMetV2Rows(
  response: AxiosResponse<WeMetConnectionRead[]>,
): AxiosResponse<WeMetConnectionRead[]> {
  if (Array.isArray(response.data)) response.data.forEach(assertWeMetV2Enriched);
  return response;
}
